import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MdHistory, MdRefresh } from 'react-icons/md';
import { getTenancyHistory } from '../../../api/services/rentalService';
import { getMyBnbBookings } from '../../../api/services/propertyService';
import CollapsibleTopPanel from '../../../components/CollapsibleTopPanel/CollapsibleTopPanel';
import './TenancyHistory.css';
import PropertyRatingModal from './PropertyRatingModal';

const formatDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const statusLabel = (status) => {
  if (!status) return '—';
  return String(status).replace(/_/g, ' ');
};

const TYPE_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'rental', label: 'Rentals' },
  { value: 'bnb', label: 'BnB' },
];

const TenancyHistory = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ratingProperty, setRatingProperty] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [rentalRes, bnbRes] = await Promise.allSettled([
        getTenancyHistory(),
        getMyBnbBookings(),
      ]);

      const rentalRows = rentalRes.status === 'fulfilled'
        ? (Array.isArray(rentalRes.value?.data) ? rentalRes.value.data : []).map((row) => ({
            ...row,
            recordType: 'rental',
          }))
        : [];

      const bnbRows = bnbRes.status === 'fulfilled'
        ? (() => {
            const payload = bnbRes.value?.data ?? bnbRes.value;
            const list = Array.isArray(payload) ? payload : payload?.data ?? [];
            if (!Array.isArray(list)) return [];
            return list
              .filter((row) => String(row?.status || '').toUpperCase() === 'CONFIRMED')
              .map((row) => ({
                ...row,
                id: row.id || row.bookingId,
                startDate: row.checkInDate || row.startDate,
                endDate: row.checkOutDate || row.endDate,
                recordType: 'bnb',
                status: row.status || 'CONFIRMED',
              }));
          })()
        : [];

      if (rentalRes.status === 'rejected' && bnbRes.status === 'rejected') {
        throw rentalRes.reason || bnbRes.reason || new Error('Failed to load tenancy history.');
      }

      const sorted = [...rentalRows, ...bnbRows].sort((a, b) => {
        const ta = new Date(a.startDate || a.createdAt || 0).getTime();
        const tb = new Date(b.startDate || b.createdAt || 0).getTime();
        return tb - ta;
      });
      setRows(sorted);
    } catch (e) {
      setRows([]);
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filteredRows = useMemo(() => {
    if (typeFilter === 'all') return rows;
    return rows.filter((row) => (row.recordType || 'rental') === typeFilter);
  }, [rows, typeFilter]);

  const summary = useMemo(() => {
    const active = filteredRows.filter((r) => String(r.status || '').toUpperCase() === 'ACTIVE').length;
    return { total: filteredRows.length, active };
  }, [filteredRows]);

  return (
    <div className="tenancy-history-page">
      <CollapsibleTopPanel id="tenant-tenancy-history-top" defaultOpenOnMobile={false}>
        <header className="tenancy-history-header">
          <div className="tenancy-history-title-block">
            <h1 className="tenancy-history-title">Tenancy history</h1>
            <p className="tenancy-history-subtitle">
              Properties and units you have rented, including current and past stays
            </p>
          </div>
          <button
            type="button"
            className="tenancy-history-refresh"
            onClick={load}
            disabled={loading}
            aria-label="Refresh"
          >
            <MdRefresh className={loading ? 'spin' : ''} />
          </button>
        </header>

        {!loading && !error && rows.length > 0 && (
          <div className="tenancy-history-toolbar">
            <div className="tenancy-history-filter-group">
              <label htmlFor="tenancy-history-type">Type</label>
              <select
                id="tenancy-history-type"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                disabled={loading}
              >
                {TYPE_FILTERS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </CollapsibleTopPanel>

      {!loading && !error && filteredRows.length > 0 && (
        <div className="tenancy-history-summary-bar" aria-live="polite">
          <span>
            <strong>{summary.total}</strong> record{summary.total === 1 ? '' : 's'}
          </span>
          {summary.active > 0 && (
            <span className="tenancy-summary-active">
              <strong>{summary.active}</strong> active
            </span>
          )}
        </div>
      )}

      {loading && (
        <div className="tenancy-history-state">
          <div className="tenancy-skeleton-line wide" />
          <div className="tenancy-skeleton-line" />
          <div className="tenancy-skeleton-line" />
        </div>
      )}

      {!loading && error && (
        <div className="tenancy-history-error" role="alert">
          <div className="tenancy-history-error-title">Could not load tenancy history</div>
          <p className="tenancy-history-error-msg">{error.message || 'Something went wrong.'}</p>
          <button type="button" className="tenancy-history-retry" onClick={load}>
            Try again
          </button>
        </div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div className="tenancy-history-empty">
          <div className="tenancy-history-empty-icon">
            <MdHistory />
          </div>
          <div className="tenancy-history-empty-title">No tenancies yet</div>
          <p className="tenancy-history-empty-text">
            When you are assigned to a unit, your stays will appear here with property, unit, and dates.
          </p>
        </div>
      )}

      {!loading && !error && rows.length > 0 && filteredRows.length === 0 && (
        <div className="tenancy-history-empty">
          <div className="tenancy-history-empty-icon">
            <MdHistory />
          </div>
          <div className="tenancy-history-empty-title">No {typeFilter === 'bnb' ? 'BnB' : 'rental'} history yet</div>
          <p className="tenancy-history-empty-text">
            Try another filter to see your full tenancy history.
          </p>
        </div>
      )}

      {!loading && !error && filteredRows.length > 0 && (
        <div className="tenancy-history-list" role="list">
          {filteredRows.map((row) => {
            const st = String(row.status || '').toUpperCase();
            const isActive = st === 'ACTIVE';
            const endDisplay = row.endDate ? formatDate(row.endDate) : isActive ? 'Current' : '—';
            const noticeBits = [];
            const isBnb = row.recordType === 'bnb';
            if (row.onNotice) noticeBits.push('On notice');
            if (row.noticeStatus && String(row.noticeStatus).toUpperCase() !== 'NONE') {
              noticeBits.push(statusLabel(row.noticeStatus));
            }
            if (row.noticeMoveOutMonth != null && row.noticeMoveOutYear != null) {
              noticeBits.push(`Planned move: ${row.noticeMoveOutMonth}/${row.noticeMoveOutYear}`);
            }

            return (
              <article key={row.id} className="tenancy-card" role="listitem">
                <div className="tenancy-card-top">
                  <div className="tenancy-card-titles">
                    <h2 className="tenancy-property-name">{row.propertyName || 'Property'}</h2>
                    <p className="tenancy-unit-line">{row.unitName || row.unitNumber || 'Unit'}</p>
                    <p className="tenancy-type-line">{isBnb ? 'BnB stay' : 'Rental tenancy'}</p>
                  </div>
                  <span className={`tenancy-status-badge tenancy-status-${st.toLowerCase()}`}>
                    {statusLabel(row.status)}
                  </span>
                </div>
                <dl className="tenancy-dl">
                  <div className="tenancy-dl-row">
                    <dt>Started</dt>
                    <dd>{formatDate(row.startDate) || '—'}</dd>
                  </div>
                  <div className="tenancy-dl-row">
                    <dt>Ended</dt>
                    <dd>{endDisplay}</dd>
                  </div>
                </dl>
                {isBnb && row.bookingCode && (
                  <div className="tenancy-booking-code-row">
                    <span className="tenancy-booking-code-label">Code:</span>
                    <span className="tenancy-booking-code-value">{row.bookingCode}</span>
                  </div>
                )}
                {noticeBits.length > 0 && (
                  <div className="tenancy-notice-banner" role="status">
                    {noticeBits.join(' · ')}
                  </div>
                )}
                {row.noticeReason ? (
                  <p className="tenancy-notice-reason">
                    <span className="tenancy-notice-label">Notice reason</span>
                    {row.noticeReason}
                  </p>
                ) : null}

                <div className="tenancy-card-actions">
                  <button
                    type="button"
                    className="tenancy-rate-btn"
                    onClick={() => setRatingProperty({ id: row.propertyId, name: row.propertyName })}
                    disabled={!row.propertyId}
                  >
                    Rate Property
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {ratingProperty && (
        <PropertyRatingModal
          isOpen={!!ratingProperty}
          onClose={() => setRatingProperty(null)}
          propertyId={ratingProperty.id}
          propertyName={ratingProperty.name}
        />
      )}
    </div>
  );
};

export default TenancyHistory;
